import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { checklistTemplateAPI, checklistDocumentAPI, masterDataAPI, productMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "select2/dist/css/select2.min.css";
import "select2";
import $ from "jquery";
import "./RaiseComplaint.css";
import "./AddEditCountryMaster.css";

const normalizeTemplate = (item) => ({
  templateCode: item.templateCode || item.TemplateCode || item.TEMPLATECODE || "",
  templateId: item.templateId || item.TemplateId || item.TEMPLATEID || "",
  templateName: item.templateName || item.TemplateName || item.TEMPLATENAME || "",
  templateDescription: item.templateDescription || item.TemplateDescription || item.TEMPLATEDESCRIPTION || "",
  // Some API responses return only market name in edit payload.
  // Keep a raw market token here so we can resolve to actual marketCode once market master loads.
  marketCode:
    item.marketCode ||
    item.MarketCode ||
    item.MARKETCODE ||
    item.market ||
    item.Market ||
    item.MARKET ||
    item.marketName ||
    item.MarketName ||
    item.MARKETNAME ||
    "0000",
  marketName:
    item.marketName ||
    item.MarketName ||
    item.MARKETNAME ||
    "",
  productTypeCode: item.productTypeCode || item.ProductTypeCode || item.PRODUCTTYPECODE || "0000",
  catgCode: item.catgCode || item.CatgCode || item.CATGCODE || "0000",
  // Display / fallback token when API returns category name (Business Vertical) but code needs resolution after categories load.
  catgName:
    item.catgName ||
    item.CatgName ||
    item.CATGNAME ||
    item.businessVertical ||
    item.BusinessVertical ||
    item.divisionName ||
    item.DivisionName ||
    "",
  productQualityCode: item.productQualityCode || item.ProductQualityCode || item.ProductqualityCODE || "0000",
  documentSLNo: item.documentSLNo || item.DocumentSLNo || item.DOCUMENTSLNO || null,
  bvCode: item.bvCode || item.BVCode || item.BVCODE || "",
  productCode: item.productCode || item.ProductCode || item.PRODUCTCODE || "",
  version: item.version || item.Version || item.VERSION || "",
  isActive: item.isActive ?? item.IsActive ?? true,
  displayOrder: item.displayOrder || item.DisplayOrder || item.DISPLAYORDER || 0,
});

const resolveMarketCodeFromValue = (rawValue, markets = []) => {
  const raw = String(rawValue || "").trim();
  if (!raw || raw === "0000" || !Array.isArray(markets) || markets.length === 0) return null;
  const lowerRaw = raw.toLowerCase();
  const pickCode = (m) =>
    String(
      m.marketCode ||
      m.MarketCode ||
      m.MARKETCODE ||
      m.code ||
      m.Code ||
      m.CODE ||
      ""
    ).trim();
  const pickName = (m) =>
    String(
      m.marketName ||
      m.MarketName ||
      m.MARKETNAME ||
      m.name ||
      m.Name ||
      m.NAME ||
      ""
    ).trim();
  const byCode = markets.find((m) => {
    const code = pickCode(m);
    return code && code.toLowerCase() === lowerRaw;
  });
  if (byCode) return pickCode(byCode);
  const byName = markets.find((m) => {
    const name = pickName(m);
    return name && name.toLowerCase() === lowerRaw;
  });
  if (byName) return pickCode(byName);
  return null;
};

const pickCategoryCode = (cat) =>
  String(
    cat?.productCategoryId ??
      cat?.ProductCategoryId ??
      cat?.catgCode ??
      cat?.CatgCode ??
      cat?.CATGCODE ??
      ""
  ).trim();

const pickCategoryLabel = (cat) =>
  String(
    cat?.productCategoryDescription ||
      cat?.productcategoryDescription ||
      cat?.ProductCategoryDescription ||
      cat?.catgName ||
      cat?.CatgName ||
      cat?.CATGNAME ||
      ""
  ).trim();

/** Resolve Business Vertical (product category id / CatgCode) from code or label for the current Market's category list. */
const resolveCategoryCodeFromValue = (rawValue, categories = []) => {
  const raw = String(rawValue || "").trim();
  if (!raw || raw === "0000" || !Array.isArray(categories) || categories.length === 0) return null;
  const lowerRaw = raw.toLowerCase();
  const byCode = categories.find((c) => {
    const code = pickCategoryCode(c);
    return code && code.toLowerCase() === lowerRaw;
  });
  if (byCode) return pickCategoryCode(byCode);
  const byLabel = categories.find((c) => {
    const label = pickCategoryLabel(c);
    return label && label.toLowerCase() === lowerRaw;
  });
  if (byLabel) return pickCategoryCode(byLabel);
  return null;
};

const AddEditChecklistTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateCode } = useParams();
  const isEditMode = !!templateCode;
  const isCopyDraft = Boolean(location.state?.isCopyDraft);
  const copyFromTemplateCode = location.state?.copyFromTemplateCode || "";

  const [template, setTemplate] = useState({
    templateCode: "",
    templateId: "",
    templateName: "",
    templateDescription: "",
    marketCode: "0000",
    marketName: "",
    productTypeCode: "0000",
    catgCode: "0000",
    catgName: "",
    productQualityCode: "0000",
    documentSLNo: null,
    bvCode: "",
    productCode: "",
    version: "",
    isActive: true,
    displayOrder: 0,
  });

  const [markets, setMarkets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [productQualities, setProductQualities] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const catgSelectRef = useRef(null);
  const productTypeSelectRef = useRef(null);
  const productQualitySelectRef = useRef(null);
  const bvSelectRef = useRef(null);
  const documentSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    loadMarkets();
    loadProductTypes();
    loadProductQualities();
    loadBusinessVerticals();
    loadDocuments();
    if (isEditMode) {
      loadTemplate();
    } else {
      // Auto-generate Template Code for new templates
      loadNextTemplateCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateCode]);

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 100);
      const normalizeMarkets = (items) =>
        (Array.isArray(items) ? items : []).map((m) => ({
          ...m,
          marketCode:
            m.marketCode ||
            m.MarketCode ||
            m.MARKETCODE ||
            m.marketcode ||
            m.code ||
            m.Code ||
            m.CODE ||
            "",
          marketName:
            m.marketName ||
            m.MarketName ||
            m.MARKETNAME ||
            m.marketname ||
            m.name ||
            m.Name ||
            m.NAME ||
            "",
        }));
      if (response?.success) {
        // Check different possible response structures
        if (response.data?.data && Array.isArray(response.data.data)) {
          setMarkets(normalizeMarkets(response.data.data));
        } else if (Array.isArray(response.data)) {
          setMarkets(normalizeMarkets(response.data));
        } else if (response.data?.markets && Array.isArray(response.data.markets)) {
          setMarkets(normalizeMarkets(response.data.markets));
        } else {
          setMarkets([]);
        }
      } else if (Array.isArray(response?.data)) {
        setMarkets(normalizeMarkets(response.data));
      } else {
        setMarkets([]);
      }
    } catch (e) {
      console.error("Error loading markets:", e);
      setMarkets([]);
    }
  };

  const loadCategories = async (marketCode) => {
    try {
      if (!marketCode || marketCode === "0000") {
        setCategories([]);
        return;
      }
      const response = await productMasterAPI.getCategories(true, marketCode);
      let list = [];
      if (response?.success && response.data) {
        list = Array.isArray(response.data) ? response.data : [];
      }
      if (list.length === 0 && response?.data?.data && Array.isArray(response.data.data)) {
        list = response.data.data;
      }
      if (list.length === 0 && response?.Data && Array.isArray(response.Data)) {
        list = response.Data;
      }
      setCategories(list);
    } catch (e) {
      console.error("Error loading categories:", e);
      setCategories([]);
    }
  };

  const loadProductTypes = async () => {
    try {
      const response = await productMasterAPI.getProductTypes(null, true);
      if (response?.success && response.data) {
        setProductTypes(response.data);
      } else if (response?.data) {
        setProductTypes(Array.isArray(response.data) ? response.data : []);
      }
    } catch (e) {
      console.error("Error loading product types:", e);
    }
  };

  const loadProductQualities = async () => {
    try {
      // TODO: Add API endpoint for Product Quality Master
      // For now, using a placeholder
      setProductQualities([
        { productQualityCode: "0000", productQualityName: "Default" },
      ]);
    } catch (e) {
      console.error("Error loading product qualities:", e);
    }
  };

  const loadBusinessVerticals = async () => {
    try {
      const response = await masterDataAPI.getVerticals();
      if (response?.success && response.data) {
        setBusinessVerticals(response.data);
      }
    } catch (e) {
      console.error("Error loading business verticals:", e);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await checklistDocumentAPI.getAll({
        status: "active",
        pageNumber: 1,
        pageSize: 100,
      });
      if (response?.success && response.data?.data) {
        setDocuments(response.data.data);
      }
    } catch (e) {
      console.error("Error loading documents:", e);
    }
  };

  const loadNextTemplateCode = async () => {
    try {
      const response = await checklistTemplateAPI.getNextCode();
      if (response && response.success && response.data) {
        setTemplate((prev) => ({ ...prev, templateCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next template code:", e);
      // Set a default template code if API fails
      setTemplate((prev) => ({ ...prev, templateCode: `TEMPLATE-${Date.now()}` }));
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await checklistTemplateAPI.getByCode(templateCode);
      if (response && response.success && response.data) {
        const normalizedTemplate = normalizeTemplate(response.data);
        const resolvedMarketCode =
          resolveMarketCodeFromValue(normalizedTemplate.marketCode, markets) ||
          resolveMarketCodeFromValue(normalizedTemplate.marketName, markets) ||
          normalizedTemplate.marketCode;
        const normalizedForState = {
          ...normalizedTemplate,
          marketCode: resolvedMarketCode || "0000",
        };
        console.log("[AddEditChecklistTemplate] edit template payload:", response.data);
        console.log("[AddEditChecklistTemplate] normalized template:", normalizedForState);
        setTemplate(normalizedForState);
        // Load categories for the template's market code
        if (normalizedForState.marketCode && normalizedForState.marketCode !== "0000") {
          await loadCategories(normalizedForState.marketCode);
          // After categories are loaded, ensure the category value is set
          // This will be handled by the useEffect that watches categories and template.catgCode
        }
      } else {
        navigate("/checklist-templates");
      }
    } catch (e) {
      console.error("Error loading template:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load template details", "error");
      }
      navigate("/checklist-templates");
    } finally {
      setLoading(false);
    }
  };

  const handleMarketChange = (value) => {
    const selectedMarketCode = String(value || "0000").trim();
    setTemplate((prev) => ({
      ...prev,
      marketCode: selectedMarketCode,
      catgCode: "0000",
      catgName: "",
    }));
  };

  // Edit mode safety: API may return market name/text instead of code (e.g., "Domestic").
  // Normalize to actual marketCode so Select2 can populate selected value.
  useEffect(() => {
    if (!markets || markets.length === 0) return;
    const current = String(template.marketCode || "").trim();
    if (!current) return;
    const hasExactCode = markets.some((m) => {
      const code = String(m.marketCode || m.MarketCode || "").trim();
      return code && code.toLowerCase() === current.toLowerCase();
    });
    if (hasExactCode) return;
    const resolved =
      resolveMarketCodeFromValue(current, markets) ||
      resolveMarketCodeFromValue(template.marketName, markets);
    if (resolved && resolved !== current) {
      setTemplate((prev) => ({ ...prev, marketCode: resolved }));
    }
  }, [markets, template.marketCode, template.marketName]);

  // Load categories when market code changes
  useEffect(() => {
    if (template.marketCode && template.marketCode !== "0000") {
      loadCategories(template.marketCode);
    } else {
      setCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.marketCode]);

  useEffect(() => {
    if (!catgSelectRef.current) return;
    
    const timer = setTimeout(() => {
      const $select = $(catgSelectRef.current);
      if (!$select.length) return;
      
      // Enable/disable based on market selection
      const isDisabled = !template.marketCode || template.marketCode === "0000";
      
      const isSelect2Initialized = $select.data("select2");
      
      // If Select2 is already initialized, destroy and reinitialize to pick up new options
      if (isSelect2Initialized) {
        try {
          $select.off("change select2:select");
          $select.select2("destroy");
        } catch (e) {
          console.warn("Error destroying Select2:", e);
        }
      }
      
      // Initialize Select2 (React has already rendered the options in the DOM)
      $select.prop("disabled", isDisabled);
      
      $select.select2({
        width: "100%",
        placeholder: isDisabled ? "Please select Market first" : "Select Business Vertical",
        allowClear: false,
      });
      
      $select.on("change", function (e) {
        setTemplate((prev) => ({
          ...prev,
          catgCode: e.target.value || "0000",
          catgName: "",
        }));
      });
      
      // Set value after Select2 is initialized
      // Use a small delay to ensure options are in the DOM
      setTimeout(() => {
        if (!isDisabled && categories.length > 0) {
          const resolvedCatg =
            resolveCategoryCodeFromValue(template.catgCode, categories) ||
            resolveCategoryCodeFromValue(template.catgName, categories);
          const effectiveCatg = resolvedCatg || template.catgCode;
          if (resolvedCatg && resolvedCatg !== template.catgCode) {
            setTemplate((prev) => ({ ...prev, catgCode: resolvedCatg }));
          }
          if (effectiveCatg && effectiveCatg !== "0000") {
            const categoryExists = categories.some(
              (cat) => pickCategoryCode(cat) === String(effectiveCatg).trim()
            );
            if (categoryExists) {
              $select.val(String(effectiveCatg).trim()).trigger("change.select2");
            } else {
              $select.val("0000").trigger("change.select2");
              if (template.catgCode !== "0000") {
                setTemplate((prev) => ({ ...prev, catgCode: "0000" }));
              }
            }
          } else {
            $select.val("0000").trigger("change.select2");
          }
        } else {
          $select.val("0000").trigger("change.select2");
        }
      }, 100);
    }, 200);
    
    return () => {
      clearTimeout(timer);
      if (catgSelectRef.current) {
        const $select = $(catgSelectRef.current);
        if ($select.data("select2")) {
          try {
            $select.off("change");
            $select.select2("destroy");
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }
    };
  }, [categories, template.catgCode, template.catgName, template.marketCode]);

  useEffect(() => {
    if (productTypeSelectRef.current && productTypes.length > 0) {
      const $select = $(productTypeSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "Select Product Type",
        allowClear: false,
      });
      $select.on("change", function (e) {
        setTemplate((prev) => ({ ...prev, productTypeCode: e.target.value || "0000" }));
      });
      if (template.productTypeCode) {
        $select.val(template.productTypeCode).trigger("change");
      }
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [productTypes, template.productTypeCode]);

  useEffect(() => {
    if (bvSelectRef.current && businessVerticals.length > 0) {
      const $select = $(bvSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "Select Business Vertical",
        allowClear: true,
      });
      $select.on("change", function (e) {
        setTemplate((prev) => ({ ...prev, bvCode: e.target.value || "" }));
      });
      if (template.bvCode) {
        $select.val(template.bvCode).trigger("change");
      }
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [businessVerticals, template.bvCode]);

  useEffect(() => {
    if (documentSelectRef.current && documents.length > 0) {
      const $select = $(documentSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "Select Document (Optional)",
        allowClear: true,
      });
      $select.on("change", function (e) {
        setTemplate((prev) => ({
          ...prev,
          documentSLNo: e.target.value ? parseInt(e.target.value) : null,
        }));
      });
      if (template.documentSLNo) {
        $select.val(template.documentSLNo.toString()).trigger("change");
      }
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [documents, template.documentSLNo]);

  useEffect(() => {
    if (productQualitySelectRef.current && productQualities.length > 0) {
      const $select = $(productQualitySelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "Select Product Quality",
        allowClear: false,
      });
      $select.on("change", function (e) {
        setTemplate((prev) => ({ ...prev, productQualityCode: e.target.value || "0000" }));
      });
      if (template.productQualityCode) {
        $select.val(template.productQualityCode).trigger("change");
      }
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [productQualities, template.productQualityCode]);

  const handleChange = (field, value) => {
    setTemplate((prev) => ({ ...prev, [field]: value }));
  };

  const categoryBelongsToLoadedMarket = (catgCode) =>
    categories.some((cat) => pickCategoryCode(cat) === String(catgCode || "").trim());

  /** Normalize trim + resolve Market / Business Vertical so DB receives consistent codes for the selected Market. */
  const buildSavePayload = () => {
    const templateName = String(template.templateName || "").trim();
    const templateDescription = String(template.templateDescription || "").trim();
    const marketCode =
      resolveMarketCodeFromValue(String(template.marketCode || "").trim(), markets) ||
      resolveMarketCodeFromValue(String(template.marketName || "").trim(), markets) ||
      String(template.marketCode || "").trim();
    const catgCode =
      resolveCategoryCodeFromValue(String(template.catgCode || "").trim(), categories) ||
      resolveCategoryCodeFromValue(String(template.catgName || "").trim(), categories) ||
      String(template.catgCode || "").trim();

    return {
      templateCode: String(template.templateCode || "").trim(),
      templateId: template.templateId ? String(template.templateId).trim() : null,
      templateName,
      templateDescription: templateDescription || null,
      marketCode: marketCode || "0000",
      catgCode: catgCode || "0000",
      productTypeCode: String(template.productTypeCode || "0000").trim() || "0000",
      productQualityCode: String(template.productQualityCode || "0000").trim() || "0000",
      documentSLNo: template.documentSLNo ?? null,
      bvCode: template.bvCode ? String(template.bvCode).trim() : null,
      productCode: template.productCode ? String(template.productCode).trim() : null,
      version: template.version ? String(template.version).trim() : null,
      isActive: Boolean(template.isActive),
      displayOrder: template.displayOrder ?? 0,
    };
  };

  const validatePayload = (payload) => {
    if (!payload.templateName) {
      Swal.fire("Error", "Template Name is required", "error");
      return false;
    }
    if (!payload.marketCode || payload.marketCode === "0000") {
      Swal.fire("Error", "Market is required", "error");
      return false;
    }
    if (!payload.catgCode || payload.catgCode === "0000") {
      Swal.fire("Error", "Business Vertical is required", "error");
      return false;
    }
    if (!categoryBelongsToLoadedMarket(payload.catgCode)) {
      Swal.fire(
        "Error",
        "Business Vertical must match the selected Market. Choose a valid Business Vertical for this Market.",
        "error"
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    const payload = buildSavePayload();
    if (!validatePayload(payload)) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || currentUser?.userName || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await checklistTemplateAPI.update(templateCode, payload, createdBy);
      } else {
        response = await checklistTemplateAPI.create(payload, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Template saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/checklist-templates");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save template",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving template:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save template. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/checklist-templates"
            onClick={(e) => {
              e.preventDefault();
              navigate("/checklist-templates");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Checklist Configuration
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Template" : "Add Template"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/checklist-templates")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {isCopyDraft && templateCode && (
        <Alert severity="info" className="mb-3">
          Copy draft — template header is saved as <strong>{templateCode}</strong>.{" "}
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              navigate(
                `/checklist-questions/tree?templateCode=${encodeURIComponent(templateCode)}`,
                { state: { isCopyDraft: true, copyFromTemplateCode } }
              )
            }
          >
            Edit questions (save from tree page)
          </Button>
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Market <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  className="form-select form-control-light filter-select text-start"
                  style={{ height: "40px", textAlign: "left" }}
                  value={template.marketCode || "0000"}
                  onChange={(e) => handleMarketChange(e.target.value)}
                >
                  <option value="0000">Select Market</option>
                  {markets && markets.length > 0 ? (
                    markets.map((market) => {
                      const code = market.marketCode || market.MarketCode || market.MARKETCODE || "";
                      const name = market.marketName || market.MarketName || market.MARKETNAME || "";
                      return (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      );
                    })
                  ) : (
                    <option value="0000" disabled>Loading markets...</option>
                  )}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Business Vertical <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={catgSelectRef}
                  className="form-select form-control-light filter-select text-start"
                  style={{ height: "40px", textAlign: "left" }}
                  disabled={!template.marketCode || template.marketCode === "0000"}
                >
                  <option value="0000">
                    {!template.marketCode || template.marketCode === "0000"
                      ? "Please select Market first"
                      : categories.length === 0
                        ? "Loading product categories..."
                        : "Select Business Vertical"}
                  </option>
                  {categories && categories.length > 0 ? (
                    categories.map((cat) => {
                      const code = cat.productCategoryId || cat.productcategoryId || cat.catgCode || cat.CatgCode || "";
                      const name = cat.productCategoryDescription || cat.productcategoryDescription || cat.catgName || cat.CatgName || "";
                      return (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      );
                    })
                  ) : null}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Template Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={template.templateName}
                  onChange={(e) => handleChange("templateName", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter template name"
                  className="add-edit-product-textfield"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/checklist-templates")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditChecklistTemplate;
